import { CellContext, RowData } from '@tanstack/react-table';

import {
  formatDateWithTimezone,
  FormatDateWithTimezoneOptions,
} from 'common/utils';

export const valueCell =
  () =>
  <TData extends RowData>({ getValue }: CellContext<TData, unknown>) => {
    try {
      return getValue() || '-';
    } catch (error) {
      return '-';
    }
  };

export const enumCell =
  (mapping?: Record<string, string>) =>
  <TData extends RowData>({ getValue }: CellContext<TData, unknown>) => {
    try {
      const value = getValue() as string | null;
      if (!value) return '-';

      // If mapping exists and contains the value, return mapped value
      if (mapping && value in mapping) {
        return mapping[value];
      }

      // Otherwise format the original value
      return value
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toUpperCase();
    } catch (error) {
      return '-';
    }
  };

export const booleanCell =
  ({
    positive = 'Yes',
    negative = 'No',
  }: { positive?: string; negative?: string } = {}) =>
  <TData extends RowData>({ getValue }: CellContext<TData, unknown>) => {
    try {
      const value = getValue();
      if (value === true) {
        return positive;
      }

      if (value === false) {
        return negative;
      }

      return '-';
    } catch (error) {
      return '-';
    }
  };

export const dateCell =
  (options: FormatDateWithTimezoneOptions = {}) =>
  <TData extends RowData>({ getValue }: CellContext<TData, unknown>) => {
    try {
      const value = getValue();
      const formattedValue = value
        ? formatDateWithTimezone(value as Date, options)
        : '-';
      return formattedValue;
    } catch (error) {
      return '-';
    }
  };

export const numberCell =
  <TData extends RowData>({
    prefix = '',
    suffix = '',
    handler,
    fractionDigits = 2,
  }: {
    suffix?: string;
    prefix?: string;
    fractionDigits?: number;
    handler?: (
      value: number | null | undefined
    ) => number | string | null | undefined;
  } = {}) =>
  ({ getValue }: CellContext<TData, unknown>) => {
    try {
      const value = getValue();
      return value
        ? `${prefix}${handler ? handler(value as number | null | undefined) : (value as number | null)?.toFixed(fractionDigits)}${suffix}`
        : '-';
    } catch (error) {
      return '-';
    }
  };
